<script setup lang="ts">
import { computed, ref, toRef, unref, watch } from 'vue';
import { onBeforeRouteLeave, onBeforeRouteUpdate } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useForm } from 'vee-validate';
import { useQuasar } from 'quasar';
import { formatDateRelative } from '@/plugins/i18n';
import { storeToRefs } from 'pinia';

import DynamicTabs from '@/components/DynamicTabs.vue';
import ImageField from '@/components/ImageField.vue';
import LocalizedInput from '@/components/LocalizedInput.vue';
import FormActions from '@/components/FormActions.vue';
import LocalizedSelect from '@/components/base/LocalizedSelect.vue';
import StandardToggle from '@/components/StandardToggle.vue';
import ContextList from '@/components/ContextList.vue';
import ErrorBanner from '@/components/ErrorBanner.vue';
import FormStepConfiguration from '@/components/FormSteps/FormStepConfiguration.vue';
import FormGuidedConsultationTab from '@/components/FormTabs/FormGuidedConsultationTab.vue';

import type { ConsultationDto, FormDto, Locales, SectionDto } from '@/services/types/api.types';
import type { SavingOptions } from '@/services/types/file-uploader.types';
import { useCommentsStore, useCommunityStore, useFormsStore } from '@/stores';
import { useLanguageValidationErrors } from '@/composables';
import { Constants } from '@/constants';
import { wrapSavingOperation } from '@/helpers/notifications.helper';
import {
  dynamicTabsNavigationGuard,
  formSaveImageGuard,
  formSaveNavigationGuard,
  hasRouteChanged,
  type DynamicTabsNavigationGuardCallbacks,
  resetFormToItem,
} from '@/helpers/form.helper';
import { useWatchItemLoading } from '@/composables/watch-item-loading';
import { useAnswersErrorKey } from '@/composables/useAnswersErrorKey';
import { provideEditLocale } from '@/composables/data-display-utils';
import { formValidationSchema } from '@/validation/formSchema';

// CONST
const { t } = useI18n();
const $q = useQuasar();

// STORE
const formsStore = useFormsStore();
const commentsStore = useCommentsStore();
const communityStore = useCommunityStore();
const { community } = storeToRefs(communityStore);

// PROPS
const props = withDefaults(
  defineProps<{
    formId: number;
    commentsEnabled?: boolean;
    isSuperAdmin?: boolean;
    languageEnabled?: Locales[];
    section?: SectionDto | null;
    consultation?: ConsultationDto | null;
  }>(),
  {
    commentsEnabled: false,
    isSuperAdmin: false,
    languageEnabled: () => [],
    section: null,
    consultation: null,
  },
);

const formId = toRef(props, 'formId');
const languageEnabled = toRef(props, 'languageEnabled');

// REFS
const openedTab = ref<string>('infos');
const dynamicTabsRef = ref<InstanceType<typeof DynamicTabs> | null>(null);

// LANGUE
const editLocale = provideEditLocale(toRef(() => languageEnabled.value));

// FORM
const validationSchema = computed(() => formValidationSchema(languageEnabled.value));
const formContext = useForm({ validationSchema });
const { handleSubmit, errors, meta, resetForm, setFieldValue } = formContext;
const languageErrors = useLanguageValidationErrors(errors);
const { answersErrorKey } = useAnswersErrorKey(formContext);

const form = useWatchItemLoading<FormDto>(formId, formsStore, (f) => resetFormToItem(formContext, f));

// COMPUTED
const authorEmail = computed(() => form.value?.user?.email);

const typeOptions = computed(() => formsStore.getFormTypes());
const statusOptions = computed(() => formsStore.getFormStatuses());

const tabs = computed(() => {
  const tablist = [
    { id: 'infos', name: t('form.tabs.infos'), icon: 'o_info' },
    { id: 'contexts', name: t('form.tabs.contexts'), icon: 'link' },
    { id: 'steps', name: t('form.tabs.steps'), icon: 'o_view_agenda' },
    community.value?.options?.consultation?.guided_consultation
      ? { id: 'guided-consultation', name: t('form.tabs.guidedConsultation'), icon: 'o_quiz', badge: t('global.new') }
      : undefined,
  ].filter((tab): tab is NonNullable<typeof tab> => tab !== undefined);
  return tablist;
});

const isDirty = computed(() => {
  return unref(meta).dirty;
});

// LIFECYCLE
watch(
  formId,
  async (newFormId) => {
    if (newFormId == null || newFormId < 0) {
      return;
    }
    if (openedTab.value !== 'infos') {
      openedTab.value = 'infos';
      dynamicTabsRef.value?.setActiveTab('infos');
    }

    await formsStore.fetchForm(newFormId);
    const loadedForm = formsStore.getItem(newFormId);
    if (!loadedForm) {
      return;
    }

    await commentsStore.fetchCommentsByFormId(newFormId);
    if (loadedForm.type === Constants.Form.Type.OpenQuestion || loadedForm.answers?.steps?.[0]?.otherChoice) {
      await formsStore.fetchVotes(newFormId);
    }
  },
  { immediate: true },
);

// ROUTER
onBeforeRouteLeave(formSaveNavigationGuard(meta));
onBeforeRouteUpdate((to, from, next) => {
  if (!hasRouteChanged(to, from)) {
    return formSaveNavigationGuard(meta)(to, from, next);
  }
  next();
});

defineExpose({
  executeNavigationGuard,
});

// FUNCTIONS
const removeImage = async () => {
  await formsStore.removeFormImage(formId.value);
  setFieldValue('question.image', null);
};

const handleClearImage = async () => {
  formSaveImageGuard(isDirty.value, removeImage);
};

const handleSaveImage = async (options: SavingOptions) => {
  await wrapSavingOperation(async () => await formsStore.saveFormImage(formId.value, options));
};

const onSubmit = handleSubmit(async (values) => {
  console.debug('submitting', values);
  wrapSavingOperation(
    async () => await formsStore.saveForm(values as FormDto),
    () => resetForm({ values: form.value! }),
  );
});

const onCancel = () => {
  if (form.value) {
    resetForm({ values: form.value });
  } else {
    console.warn('onCancel: form is null');
  }
};
const approveIt = async () => {
  await formsStore.approve(form.value!.id, form.value?.moderation_key);
};
const refuseIt = async () => {
  $q.dialog({
    title: t('form.citizenProposal.refuseDialogTitle'),
    message: t('form.citizenProposal.refuseDialogMessage'),
    ok: {
      label: t('global.yes'),
      color: 'negative',
      flat: true,
    },
    cancel: {
      label: t('global.no'),
      color: 'primary',
      unelevated: true,
    },
  }).onOk(async () => {
    await formsStore.refuse(form.value!.id, form.value?.moderation_key);
  });
};

function executeNavigationGuard(callbackFns?: DynamicTabsNavigationGuardCallbacks) {
  dynamicTabsNavigationGuard(meta, callbackFns);
}

const handleUpdate = (tabId: string) => {
  openedTab.value = tabId;
};
</script>
<template>
  <q-page class="form-create-tab">
    <div v-if="form">
      <q-banner dense dark class="bg-accent" v-if="form.moderation_key !== null">
        Ceci est une proposition citoyenne soumise par
        <a :href="`mailto:${authorEmail}`" class="text-white">{{ authorEmail }}</a>
        <template v-slot:action>
          <q-btn unelevated color="primary" label="Approuver" @click="approveIt" />
          <q-btn unelevated color="primary" label="Refuser" @click="refuseIt" />
        </template>
      </q-banner>
      <div v-if="answersErrorKey" class="q-mb-sm">
        <error-banner :isError="true" :error="t(answersErrorKey)" />
      </div>
      <dynamic-tabs ref="dynamicTabsRef" :tabs="tabs" :active="openedTab" keep-alive @update="handleUpdate">
        <template #infos>
          <div class="column">
            <form-actions
              v-model:editLocale="editLocale"
              :onCancel="onCancel"
              :onSubmit="onSubmit"
              :languageErrors="languageErrors"
              :meta="meta"
              :language-enabled="languageEnabled"
            />
            <q-banner class="bg-secondary text-white q-mb-md" v-if="form.is_citizen_proposal"
              >Cette proposition citoyenne a été soumise par:
              <a :href="`mailto:${authorEmail}`" class="text-white">{{ authorEmail }}</a>
            </q-banner>

            <localized-select
              name="type"
              :label="t('form.type.label')"
              :editLocale="editLocale"
              :options="typeOptions"
              emit-value
              map-options
              outlined
              disabled
            />
            <localized-input
              name="question.title"
              :label="t('form.question.title')"
              :editLocale="editLocale"
              outlined
              :language_enabled="languageEnabled"
              type="textarea"
            />
            <localized-input
              name="question.content"
              :label="t('form.question.content')"
              :editLocale="editLocale"
              type="textarea"
              outlined
              :language_enabled="languageEnabled"
            />
            <image-field
              :label="t('form.question.image.label')"
              :source="form.question.image?.url"
              :target-width="528"
              :target-height="528"
              @clear-image="handleClearImage"
              @save-image="handleSaveImage"
              :form-dirty="isDirty"
              :language-enabled="languageEnabled"
              alt-text-name="question.image.altText"
            />
            <div>
              <standard-toggle
                :disabled="form.type === Constants.Form.Type.OpenQuestion || form.type === Constants.Form.Type.Map"
                name="are_comments_enabled"
                :label="t('form.question.are_comments_enabled')"
                outlined
              />
              <standard-toggle name="attachmentsOpened" :label="t('global.attachments-opened')" outlined />
            </div>
          </div>
          <div v-if="form.is_citizen_proposal && form.moderation_key === null">
            <hr />
            <p>Approbation Status {{ statusOptions.find((s) => s.value === form?.status).label }}</p>
            <p>
              Moderated by: {{ form.approvalAdmin?.first_name }}
              {{ form.approvalAdmin?.last_name }}
              [{{ formatDateRelative(form.approved_date) }}]
            </p>
          </div>
        </template>
        <template #contexts>
          <context-list
            :parent="Constants.Context.Parent.Form"
            :parent-id="form.id"
            :languageEnabled="languageEnabled"
          />
        </template>
        <template #steps>
          <div class="column">
            <form-step-configuration :form="form" :languageEnabled="languageEnabled" />
          </div>
        </template>
        <template #guided-consultation>
          <form-guided-consultation-tab
            :form-id="formId"
            :consultation="consultation"
            :language-enabled="languageEnabled"
            v-model:editLocale="editLocale"
            :meta="meta"
            :language-errors="languageErrors"
            :on-cancel="onCancel"
            :on-submit="onSubmit"
          />
        </template>
      </dynamic-tabs>
    </div>
  </q-page>
</template>
